import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { BusinessProfile, Language, MenuThemeConfig } from "../../types/Generic";
import { UiVersion } from "../../helpers/Constants";

export enum WaitListSubmitState {
  Stale,
  Requested,
  Successful,
  Failed,
}

export enum RegistrationSubmitState {
  Stale,
  Requested,
  Successful,
  Failed,
}
export interface User {
  id: string;
  currency: {
    prefix: string;
    name: string;
  };
  languages: Array<Language>;
  activities: Array<string>;
  defaultLang: string;
  name: string;
  address: string;
  brief: string;
  banner: string;
  logo: string;
  contacts: string;
  username: string;
  segment: string;
  hasSegments: boolean;
  isDemo: boolean;
  canPlaceOrder: boolean;
  theme: number;
  clientVersion: UiVersion;
  menuTheme?: MenuThemeConfig | null;
  businessProfile?: BusinessProfile | null;
  wifi?: string;
}
export interface TableContext {
  id: string;
  label: string;
  segmentId: string;
  segmentName: string;
  seats?: number | null;
}
export interface ClientState {
  user: User;
  activeLanguage: Language;
  tableNumber: string;
  table?: TableContext | null;
  waitlistSubmitState: number;
  registrationSubmitState: number;
  loading: boolean;
}

const initialState: ClientState = {
  user: {
    id: "",
    theme: UiVersion.DEMO,
    currency: { prefix: "", name: "" },
    defaultLang: "",
    name: "",
    address: "",
    brief: "",
    banner: "",
    logo: "",
    username: "",
    segment: "",
    contacts: "",
    languages: [],
    activities: [],
    hasSegments: false,
    isDemo: false,
    canPlaceOrder: false,
    clientVersion: UiVersion.DEFAULT,
    menuTheme: null,
    businessProfile: null,
  },
  activeLanguage: { name: "", iso: "" },
  tableNumber: "1",
  table: null,
  waitlistSubmitState: WaitListSubmitState.Stale,
  registrationSubmitState: RegistrationSubmitState.Stale,
  loading: false,
};

const ClientSlice = createSlice({
  name: "Client",
  initialState,
  reducers: {
    setUser: (
      state,
      action: PayloadAction<{
        user: User;
        tableNum: string;
        table?: TableContext | null;
      }>
    ) => {
      const userSettings = action.payload.user;
      const defaultLanguage = userSettings.languages.find(
        (lang) => lang.iso === userSettings.defaultLang
      );
      if (defaultLanguage) {
        state.activeLanguage = defaultLanguage;
      } else {
        state.activeLanguage = defaultLanguage ||
          userSettings.languages[0] || { name: "", iso: "" };
      }

      state.user = action.payload.user;
      state.tableNumber = action.payload.tableNum;
      state.table = action.payload.table || null;
    },
    setSelectedLanguage: (state, action: PayloadAction<Language>) => {
      state.activeLanguage = action.payload;
    },
    setWaitlistSubmitState: (
      state,
      action: PayloadAction<WaitListSubmitState>
    ) => {
      state.waitlistSubmitState = action.payload;
    },
    setRegistrationSubmitState: (
      state,
      action: PayloadAction<RegistrationSubmitState>
    ) => {
      state.registrationSubmitState = action.payload;
    },
    setLoadingState: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setMenuTheme: (state, action: PayloadAction<MenuThemeConfig | null>) => {
      state.user.menuTheme = action.payload;
    },
  },
});

export const {
  setUser,
  setSelectedLanguage,
  setWaitlistSubmitState,
  setRegistrationSubmitState,
  setLoadingState,
  setMenuTheme,
} = ClientSlice.actions;
export default ClientSlice.reducer;
